import { Group, Loader, Text, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronRight, IconCornerDownRight } from '@tabler/icons-react';
import type { SubRowsState } from './subRowsTree';
import { TREE_CELL_FILL, TREE_DEPTH_INDENT, TREE_TOGGLE_WIDTH } from './constants';

/**
 * Шеврон раскрываемой строки дерева (`subRows`) + состояние догрузки детей.
 *
 * Вид — как у строки-итога `groupBy`: одинаковая вложенность выглядит
 * одинаково, где бы её ни включили. Ошибка догрузки показывается прямо здесь —
 * пустое раскрытие без причины неотличимо от «детей нет».
 *
 * Шеврон + зазор занимают ровно `TREE_TOGGLE_WIDTH` (14 + 6 px): под это место
 * сдвигаются подписи обычных корневых строк, чтобы колонка читалась ровно.
 */
export function SubRowsToggle({
  expanded,
  state,
  onToggle,
  children,
}: {
  expanded: boolean;
  state?: SubRowsState<unknown>;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const Chevron = expanded ? IconChevronDown : IconChevronRight;
  return (
    <Group gap={6} wrap='nowrap' style={TREE_CELL_FILL}>
      <UnstyledButton
        aria-label={expanded ? 'Свернуть' : 'Развернуть'}
        // Клик по строке-родителю тоже раскрывает её (обработчик строки), поэтому
        // событие гасим: иначе два переключения подряд дали бы ноль.
        onClick={event => {
          event.stopPropagation();
          onToggle();
        }}
        style={{ display: 'flex', flexShrink: 0 }}
      >
        <Chevron size={14} />
      </UnstyledButton>
      <div style={{ minWidth: 0, flex: 1 }}>{children}</div>
      {state?.status === 'loading' && <Loader size={10} />}
      {state?.status === 'error' && (
        <Text size='xs' c='red' truncate>
          {state.error}
        </Text>
      )}
    </Group>
  );
}

/**
 * Ячейка дочерней строки дерева: уголок «↳» и содержимое.
 *
 * Одного отступа мало: в списке, где половина строк раскрыта, сдвинутая строка
 * читается как «криво выровненная», а не как «вложенная». Уголок стоит ровно
 * под началом подписи родителя (там, где у родителя кончается шеврон) и
 * «выводит» из неё подстроку — вложенность видна без чтения отступов.
 *
 * Нити дерева (├─ └─) сознательно не рисуем: строки таблицы разделены
 * отступами ячеек и имеют фиксированную высоту, линии рвались бы между ними.
 */
export function SubRowIndent({ depth, children }: { depth: number; children: React.ReactNode }) {
  return (
    <Group
      gap={6}
      wrap='nowrap'
      style={{
        ...TREE_CELL_FILL,
        // Уголок первого уровня — под подписью родителя; глубже — ещё на шаг.
        paddingLeft: TREE_TOGGLE_WIDTH + TREE_DEPTH_INDENT * (depth - 1),
      }}
    >
      <IconCornerDownRight
        size={14}
        stroke={1.5}
        color='var(--mantine-color-dimmed)'
        style={{ flexShrink: 0 }}
        aria-hidden
      />
      <div style={{ minWidth: 0, flex: 1 }}>{children}</div>
    </Group>
  );
}
